import { PREFIX_CLASS } from '../../utils/constants'
import { TCheckbox } from '../checkbox'
import TTag from '../tag'
import { merge as objectAssign, getPropByPath } from '@/utils/helper'

let columnIdSeed = 1
const PREFIX_TABLE_CLASS = `${PREFIX_CLASS}table`
const defaults = {
  default: {
    order: ''
  },
  selection: {
    width: 48,
    minWidth: 48,
    realWidth: 48,
    order: '',
    className: PREFIX_TABLE_CLASS + '-column--selection'
  },
  expand: {
    width: 48,
    minWidth: 48,
    realWidth: 48,
    order: ''
  },
  index: {
    width: 48,
    minWidth: 48,
    realWidth: 48,
    order: ''
  }
}

const forced = {
  selection: {
    renderHeader: function(h, { store }) {
      return (
        <t-checkbox
          disabled={ store.states.data && store.states.data.length === 0 }
          indeterminate={ store.states.selection.length > 0 && !this.isAllSelected }
          nativeOn-click={ this.toggleAllSelection }
          checked={ this.isAllSelected } />
      )
    },
    renderCell: function(h, { row, column, store, $index }) {
      return (
        <t-checkbox
          nativeOn-click={ (event) => {
            event.stopPropagation()
            event.preventDefault()
            store.commit('rowSelectedChanged', row)
          }}
          checked={ store.isSelected(row) }
          disabled={ column.selectable ? !column.selectable.call(null, row, $index) : false }/>
      )
    },
    sortable: false,
    resizable: false
  },
  index: {
    renderHeader: function(h, { column }) {
      return column.label || '#'
    },
    renderCell: function(h, { $index, column }) {
      let i = $index + 1
      const index = column.index

      if (typeof index === 'number') {
        i = $index + index
      } else if (typeof index === 'function') {
        i = index($index)
      }

      return <div>{ i }</div>
    },
    sortable: false
  },
  expand: {
    renderHeader: function(h, { column }) {
      return column.label || ''
    },
    renderCell: function(h, { row, store }, proxy) {
      const expanded = store.states.expandRows.indexOf(row) > -1
      return <div class={ PREFIX_TABLE_CLASS + '__expand-icon '}
        on-click={ e => proxy.$_handleExpandClick(row, e) }>
        {expanded ? <t-icon icon='minus-box-outline'></t-icon> : <t-icon icon='plus-box-outline'></t-icon>}
      </div>
    },
    sortable: false,
    resizable: false,
    className: PREFIX_TABLE_CLASS + '__expand-column'
  },
  edit: {
    renderHeader: function(h, { column }) {
      return column.label || ''
    },
    renderCell: function(h, data, proxy) {
      const _self = proxy
      const showOverflowTooltip = _self.showOverflowTooltip || data.column.showOverflowTooltip
      const showTooltipWhenOverflow = _self.showTooltipWhenOverflow || data.column.showTooltipWhenOverflow
      const editing = data.store.states.editing.flag === true && data.store.states.editing.index === data.$index && data.store.states.editing.key === data.column.property
      if (editing) {
        if (_self.$scopedSlots.default) {
          return (
            <div class="cell cell--edit cell--editing">
              {_self.$scopedSlots.default(data)}
            </div>
          )
        }
        return (<div class="cell cell--edit cell--editing">
          <t-input class="cell_input-wrap" value={data.row[data.column.property]} on-blur={(e) => _self.$_setEdit(data, false, e.target.value)} on-enter={(e) => _self.$_setEdit(data, false, e.target.value)}></t-input>
        </div>)
      }
      return (
        <div class={['cell cell--edit', { 'cell--ellipsis': showOverflowTooltip || showTooltipWhenOverflow }]}>
          { data.row[data.column.property] }
          <t-icon class="cell--edit-action" icon="edit" nativeOnClick={_self.$_setEdit.bind(this, data, true)}></t-icon>
        </div>)
    },
    className: 'td__edit'
  }
}

const getDefaultColumn = function(type, options) {
  const column = {}

  objectAssign(column, defaults[type || 'default'])

  for (let name in options) {
    if (options.hasOwnProperty(name)) {
      const value = options[name]
      if (typeof value !== 'undefined') {
        column[name] = value
      }
    }
  }

  if (!column.minWidth) {
    column.minWidth = 80
  }

  column.realWidth = column.width === undefined ? column.minWidth : column.width

  return column
}

const DEFAULT_RENDER_CELL = function(h, { row, column, $index }) {
  const property = column.property
  const value = property && getPropByPath(row, property).v
  if (column && column.formatter) {
    return column.formatter(row, column, value, $index)
  }
  return value
}

const parseWidth = (width) => {
  if (width !== undefined) {
    width = parseInt(width, 10)
    if (isNaN(width)) {
      width = null
    }
  }
  return width
}

const parseMinWidth = (minWidth) => {
  if (minWidth !== undefined) {
    minWidth = parseInt(minWidth, 10)
    if (isNaN(minWidth)) {
      minWidth = 80
    }
  }
  return minWidth
}

export default {
  name: 'TTableColumn',

  props: {
    type: {
      type: String,
      default: 'default'
    },
    label: String,
    className: String,
    labelClassName: String,
    property: String,
    prop: String,
    width: {},
    minWidth: {},
    renderHeader: Function,
    renderCell: Function,
    sortable: {
      type: [String, Boolean],
      default: false
    },
    sortMethod: Function,
    sortBy: [String, Function, Array],
    resizable: {
      type: Boolean,
      default: true
    },
    context: {},
    columnKey: String,
    align: String,
    headerAlign: String,
    showTooltipWhenOverflow: Boolean,
    showOverflowTooltip: Boolean,
    fixed: [Boolean, String],
    formatter: Function,
    selectable: Function,
    reserveSelection: Boolean,
    filterMethod: Function,
    filteredValue: Array,
    filters: Array,
    filterPlacement: String,
    filterMultiple: {
      type: Boolean,
      default: true
    },
    index: [Number, Function],
    sortOrders: {
      type: Array,
      default() {
        return ['ascending', 'descending', null]
      },
      validator(val) {
        return val.every(order => ['ascending', 'descending', null].indexOf(order) > -1)
      }
    },
    editable: Boolean
  },

  data() {
    return {
      isSubColumn: false,
      isEdit: false,
      columns: []
    }
  },

  beforeCreate() {
    this.row = {}
    this.column = {}
    this.$index = 0
  },

  components: {
    TCheckbox,
    TTag
  },

  computed: {
    owner() {
      let parent = this.$parent
      while (parent && !parent.tableId) {
        parent = parent.$parent
      }
      return parent
    },
    columnOrTableParent() {
      let parent = this.$parent
      while (parent && !parent.tableId && !parent.columnId) {
        parent = parent.$parent
      }
      return parent
    }
  },

  created() {
    this.customRender = this.$options.render
    this.$options.render = h => h('div', this.$slots.default)

    let parent = this.columnOrTableParent
    let owner = this.owner
    this.isSubColumn = owner !== parent
    this.columnId = (parent.tableId || parent.columnId) + '_column_' + columnIdSeed++

    let type = this.type

    const width = parseWidth(this.width)
    const minWidth = parseMinWidth(this.minWidth)

    let isColumnGroup = false
    let column = getDefaultColumn(type, {
      id: this.columnId,
      columnKey: this.columnKey,
      label: this.label,
      className: this.className || '',
      labelClassName: this.labelClassName,
      property: this.prop || this.property,
      type,
      renderCell: this.renderCell,
      renderHeader: this.renderHeader,
      minWidth,
      width,
      isColumnGroup,
      context: this.context,
      align: this.align ? 'is-' + this.align : null,
      headerAlign: this.headerAlign ? 'is-' + this.headerAlign : (this.align ? 'is-' + this.align : null),
      sortable: this.sortable === '' ? true : this.sortable,
      sortMethod: this.sortMethod,
      sortBy: this.sortBy,
      resizable: this.resizable,
      showOverflowTooltip: this.showOverflowTooltip || owner.allShowOverflowTooltip,
      showTooltipWhenOverflow: this.showTooltipWhenOverflow || owner.allShowTooltipWhenOverflow,
      formatter: this.formatter,
      selectable: this.selectable,
      reserveSelection: this.reserveSelection,
      fixed: this.fixed === '' ? true : this.fixed,
      filterMethod: this.filterMethod,
      filters: this.filters,
      filterable: this.filters || this.filterMethod,
      filterMultiple: this.filterMultiple,
      filterOpened: false,
      filteredValue: this.filteredValue || [],
      filterPlacement: this.filterPlacement || '',
      index: this.index,
      sortOrders: this.sortOrders,
      editable: this.editable
    })
    let source = forced[type] || {}
    Object.keys(source).forEach((prop) => {
      let value = source[prop]
      if (value !== undefined) {
        if (prop === 'renderHeader') {
          if (type === 'selection' && column[prop]) {
            console.warn('[Warn][TableColumn]Selection column doesn\'t allow to set render-header function.')
          } else {
            value = column[prop] || value
          }
        }
        column[prop] = prop === 'className' ? `${column[prop]} ${value}` : value
      }
    })

    // Deprecation warning for renderHeader property
    if (this.renderHeader) {
      console.warn('[Warn][TableColumn]Comparing to render-header, scoped-slot header is easier to use. We recommend users to use scoped-slot header.')
    }

    this.columnConfig = column
    let renderCell = column.renderCell
    let _self = this

    if (type === 'expand') {
      owner.renderExpanded = function(h, data) {
        return _self.$scopedSlots.default
          ? _self.$scopedSlots.default(data)
          : _self.$slots.default
      }

      column.renderCell = function(h, data) {
        return <div class="cell">{ renderCell(h, data, this._renderProxy) }</div>
      }

      return
    }
    if (type === 'edit') {
      column.renderCell = function(h, data) {
        return renderCell(h, data, _self)
      }
      return
    }
    column.renderCell = function(h, data, proxy) {
      if (_self.$scopedSlots.default && !_self.editable) {
        renderCell = () => _self.$scopedSlots.default(data)
      }

      if (!renderCell) {
        renderCell = DEFAULT_RENDER_CELL
      }
      const children = [
        _self.$_renderTreeCell(data),
        renderCell(h, data)
      ]
      const showOverflowTooltip = _self.showOverflowTooltip || data.column.showOverflowTooltip
      const showTooltipWhenOverflow = _self.showTooltipWhenOverflow || data.column.showTooltipWhenOverflow
      return showOverflowTooltip || showTooltipWhenOverflow
        ? <div class="cell cell--ellipsis" style={ { width: (data.column.realWidth || data.column.width) - 2 + 'px' } }>{ children }</div>
        : (<div class="cell">
          { children }
        </div>)
    }
  },

  destroyed() {
    if (!this.$parent) return
    const parent = this.$parent
    this.owner.store.commit('removeColumn', this.columnConfig, this.isSubColumn ? parent.columnConfig : null)
  },

  watch: {
    label(newVal) {
      if (this.columnConfig) {
        this.columnConfig.label = newVal
      }
    },

    prop(newVal) {
      if (this.columnConfig) {
        this.columnConfig.property = newVal
      }
    },

    property(newVal) {
      if (this.columnConfig) {
        this.columnConfig.property = newVal
      }
    },

    filters(newVal) {
      if (this.columnConfig) {
        this.columnConfig.filters = newVal
      }
    },

    filterMultiple(newVal) {
      if (this.columnConfig) {
        this.columnConfig.filterMultiple = newVal
      }
    },

    align(newVal) {
      if (this.columnConfig) {
        this.columnConfig.align = newVal ? 'is-' + newVal : null

        if (!this.headerAlign) {
          this.columnConfig.headerAlign = newVal ? 'is-' + newVal : null
        }
      }
    },

    headerAlign(newVal) {
      if (this.columnConfig) {
        this.columnConfig.headerAlign = 'is-' + (newVal || this.align)
      }
    },

    width(newVal) {
      if (this.columnConfig) {
        this.columnConfig.width = parseWidth(newVal)
        this.owner.store.scheduleLayout()
      }
    },

    minWidth(newVal) {
      if (this.columnConfig) {
        this.columnConfig.minWidth = parseMinWidth(newVal)
        this.owner.store.scheduleLayout()
      }
    },

    fixed(newVal) {
      if (this.columnConfig) {
        this.columnConfig.fixed = newVal
        this.owner.store.scheduleLayout(true)
      }
    },

    sortable(newVal) {
      if (this.columnConfig) {
        this.columnConfig.sortable = newVal
      }
    },

    index(newVal) {
      if (this.columnConfig) {
        this.columnConfig.index = newVal
      }
    },

    formatter(newVal) {
      if (this.columnConfig) {
        this.columnConfig.formatter = newVal
      }
    },

    className(newVal) {
      if (this.columnConfig) {
        this.columnConfig.className = newVal
      }
    },

    labelClassName(newVal) {
      if (this.columnConfig) {
        this.columnConfig.labelClassName = newVal
      }
    }
  },

  methods: {
    $_setEdit(data, flag, value = data.row[data.column.property]) {
      this.isEdit = flag
      if (flag === false) {
        data.row[data.column.property] = value
      }
      this.owner.store.commit('setEdit', {
        index: data.$index,
        key: data.column.property,
        flag
      })
    },
    $_renderTreeCell(data) {
      if (!data.treeNode) return null
      const ele = []
      ele.push(<span class={PREFIX_TABLE_CLASS + '__indent'} style={{ 'padding-left': data.treeNode.indent + 'px' }}></span>)
      if (data.treeNode.hasChildren) {
        ele.push(<div class={ [PREFIX_TABLE_CLASS + '__expand-icon', PREFIX_TABLE_CLASS + '__tree-icon', data.treeNode.expanded ? PREFIX_TABLE_CLASS + '__expand-icon--expanded' : '']}
          on-click={this.$_handleTreeExpandIconClick.bind(this, data)}>
          { data.treeNode.expanded ? <t-icon icon="menu-down-outline"></t-icon> : <t-icon icon="menu-right-outline"></t-icon> }
        </div>)
      } else {
        ele.push(<span class={PREFIX_TABLE_CLASS + '__placeholder'}></span>)
      }
      return ele
    },

    $_handleTreeExpandIconClick(data, e) {
      e.stopPropagation()
      if (data.store.states.lazy && !data.treeNode.loaded) {
        data.store.loadData(data.row, data.treeNode)
      } else {
        data.store.toggleTreeExpansion(data.treeNode.rowKey)
      }
    }
  },

  mounted() {
    const owner = this.owner
    const parent = this.columnOrTableParent
    let columnIndex

    if (!this.isSubColumn) {
      columnIndex = [].indexOf.call(parent.$refs.hiddenColumns.children, this.$el)
    } else {
      columnIndex = [].indexOf.call(parent.$el.children, this.$el)
    }

    if (this.$scopedSlots.header) {
      if (this.type === 'selection') {
        console.warn('[Warn][TableColumn]Selection column doesn\'t allow to set scoped-slot header.')
      } else {
        this.columnConfig.renderHeader = (h, scope) => this.$scopedSlots.header(scope)
      }
    }
    owner.store.commit('insertColumn', this.columnConfig, columnIndex, this.isSubColumn ? parent.columnConfig : null)
  }
}



// WEBPACK FOOTER //
// ./src/module/table/table-column.js